const cities = [
  { name: "Lafayette", zips: "70501, 70503, 70506, 70507, 70508" },
  { name: "Youngsville", zips: "70592" },
  { name: "Broussard", zips: "70518" },
  { name: "Carencro", zips: "70520" },
  { name: "Breaux Bridge", zips: "70517" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your home, office, or campus in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function LafayetteLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Lafayette &amp; Acadiana
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Lafayette sits at the heart of Louisiana's Acadiana region — and at the center of one of the most internationally connected economies in the Gulf South. The oil and gas industry draws a constant stream of engineers, project managers, and executives into rotation assignments across West Africa, the North Sea, the Middle East, and Latin America. Schlumberger, Halliburton, Baker Hughes, and dozens of smaller oilfield service firms maintain significant operations in and around Lafayette, and their workforce routinely travels internationally on short notice. When an offshore rotation to Angola is confirmed with two weeks to departure, or a vendor meeting in Paris is added to a quarterly travel schedule, the passport process becomes urgent fast.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Beyond the energy sector, Lafayette's cultural identity drives a unique kind of international travel. Cajun and Creole heritage tourism creates strong connections to France, Nova Scotia, and French-speaking communities across the Caribbean. The Festival International de Louisiane draws performers and attendees from francophone countries worldwide, and many Lafayette families maintain ties to European and Canadian relatives that fuel regular international trips. University of Louisiana at Lafayette (ULL) sends hundreds of students abroad through exchange programs, research partnerships, and study-abroad semesters — and many discover their passport has expired, or they have never had one, only after program acceptance.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The regional economy creates a layered passport demand pattern. Energy company employees need predictable, professional processing that fits their corporate travel timelines. Faculty and graduate students at ULL need expedited service when conference travel or field research requires international movement on academic calendars. Acadiana families booking Caribbean cruises from New Orleans — just 130 miles east — often do not think about passport expiration until the cruise is booked. Retirees in communities like Youngsville and Broussard travel extensively to Europe, and their passports require periodic renewal.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Fast Passport Center was built for exactly this mix of travelers. Whether you are an oilfield engineer at a Lafayette-based service company facing a last-minute rotation, a ULL student with a study-abroad acceptance letter in hand, or a Cajun family planning a trip to France to trace your heritage — we provide the expedited, professional passport service you need. No driving to Houston for a passport agency appointment. No hoping a post office appointment opens up. Just reliable, predictable processing from a registered U.S. Department of State courier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Lafayette, LA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Acadiana &amp; Southwest Louisiana</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — serving Lafayette, Youngsville &amp; all Acadiana
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Start online, ship overnight from anywhere in Acadiana</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, <strong className="text-white">ship your documents overnight</strong> from any UPS or FedEx location in Lafayette, or use our prepaid label. Most Acadiana clients complete the entire process without leaving home.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Acadiana — start online, ship overnight, and we handle the rest. No need to drive to Houston or New Orleans for a regional agency appointment.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Louisiana Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "New Orleans", href: "/cities/new-orleans" },
                    { name: "Baton Rouge", href: "/cities/baton-rouge" },
                    { name: "Shreveport", href: "/cities/shreveport" },
                    { name: "Lake Charles", href: "/cities/lake-charles" },
                    { name: "Monroe", href: "/cities/monroe" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Lafayette Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}